/**
 * Soft accent-colored glow that follows the mouse pointer, tightening and brightening over
 * interactive elements. Elements marked `data-spotlight` also get a local highlight that tracks
 * the pointer through the `--pointer-x` / `--pointer-y` custom properties.
 *
 * Loaded from snippets/scripts.liquid when the "Cursor glow" theme setting is on. Mouse and other
 * fine pointers only; nothing runs for touch screens or visitors who prefer reduced motion.
 */

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const INTERACTIVE = [
  'a[href]',
  'button:not([disabled])',
  '[role="button"]',
  'summary',
  'label[for]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'product-card',
].join(',');

/** Fraction of the remaining distance the glow covers each frame; lower trails more. */
const FOLLOW = 0.22;

function init() {
  if (!finePointer.matches || reducedMotion.matches) return;

  const glow = document.createElement('div');
  glow.className = 'cursor-glow';
  glow.setAttribute('aria-hidden', 'true');
  document.body.append(glow);

  const controller = new AbortController();
  const { signal } = controller;

  let x = 0;
  let y = 0;
  let targetX = 0;
  let targetY = 0;
  let frame = 0;
  let placed = false;

  const render = () => {
    x += (targetX - x) * FOLLOW;
    y += (targetY - y) * FOLLOW;
    glow.style.translate = `${x}px ${y}px`;

    if (Math.abs(targetX - x) > 0.5 || Math.abs(targetY - y) > 0.5) {
      frame = requestAnimationFrame(render);
    } else {
      frame = 0;
    }
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse') return;

      targetX = event.clientX;
      targetY = event.clientY;

      // Jump straight to the first position instead of sliding in from the corner.
      if (!placed) {
        x = targetX;
        y = targetY;
        placed = true;
      }

      glow.setAttribute('data-active', '');

      const element = event.target instanceof Element ? event.target : null;
      glow.toggleAttribute('data-hover', Boolean(element?.closest(INTERACTIVE)));

      const spotlight = element?.closest('[data-spotlight]');
      if (spotlight instanceof HTMLElement) {
        const rect = spotlight.getBoundingClientRect();
        spotlight.style.setProperty('--pointer-x', `${event.clientX - rect.left}px`);
        spotlight.style.setProperty('--pointer-y', `${event.clientY - rect.top}px`);
      }

      if (!frame) frame = requestAnimationFrame(render);
    },
    { passive: true, signal }
  );

  document.documentElement.addEventListener('pointerleave', () => glow.removeAttribute('data-active'), { signal });
  document.addEventListener('pointerdown', () => glow.setAttribute('data-pressed', ''), { passive: true, signal });
  document.addEventListener('pointerup', () => glow.removeAttribute('data-pressed'), { passive: true, signal });

  // Tear down if the visitor switches to reduced motion or a touch-only pointer mid-visit.
  const teardown = () => {
    controller.abort();
    cancelAnimationFrame(frame);
    glow.remove();
  };
  reducedMotion.addEventListener('change', teardown, { once: true, signal });
  finePointer.addEventListener('change', teardown, { once: true, signal });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
